import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectBarbers } from "sagas/selectors";
import {
    barbersRecurringBlocksActions,
    barbersRecurringBlocksActionTypes,
} from "sagas/barbers/barbersRecurringBlocks";

import Button from "components/atoms/button/Button";
import BarbersDropdown from "components/atoms/barbersDropdown/BarbersDropdown";
import Tabs from "components/atoms/tabs/Tabs";
import Error from "components/atoms/error/Error";
import BlockedDays from "components/molecules/blockedDays/BlockedDays";
import ContentLayout from "components/templates/contentLayout/ContentLayout";

import dispatchWithPromise from "utils/dispatchWithPromise";
import useErrorLoader from "hooks/useErrorSelector";
import useLoaderSelector from "hooks/useLoaderSelector";

// Working week (Mon..Sat). API uses 0..6 with Sunday=0.
const WEEKDAYS = [
    { dayOfWeek: 1, label: "E Hënë" },
    { dayOfWeek: 2, label: "E Martë" },
    { dayOfWeek: 3, label: "E Merkurë" },
    { dayOfWeek: 4, label: "E Enjte" },
    { dayOfWeek: 5, label: "E Premte" },
    { dayOfWeek: 6, label: "E Shtunë" },
];

const SATURDAY_LAST_SLOT = "17:30";

const buildSlots = () => {
    const slots = [];
    for (let i = 0; i < 19; i += 1) {
        const totalMinutes = 10 * 60 + i * 30;
        const hh = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
        const mm = String(totalMinutes % 60).padStart(2, "0");
        slots.push(`${hh}:${mm}`);
    }
    return slots;
};

const SLOTS = buildSlots();

const isSaturdayDisabled = (slot) => slot > SATURDAY_LAST_SLOT;

const normalizeSlotTime = (slotTime) => slotTime.slice(0, 5); // "HH:mm:ss" -> "HH:mm"

const cellKey = (dayOfWeek, slot) => `${dayOfWeek}-${slot}`;

const TABS = [
    { id: "recurring", label: "Orari javor" },
    { id: "days-off", label: "Ditët e lira" },
];

const Schedule = () => {
    const dispatch = useDispatch();
    const { barbers } = useSelector(selectBarbers);

    const [activeTab, setActiveTab] = useState("recurring");
    const [activeBarberId, setActiveBarberId] = useState(null);
    const [blocked, setBlocked] = useState(new Set());

    const [isSaving] = useLoaderSelector(barbersRecurringBlocksActionTypes.SAVE_REQUEST);
    const [isClearing] = useLoaderSelector(
        barbersRecurringBlocksActionTypes.CLEAR_REQUEST,
    );
    const [isSaveError, saveError] = useErrorLoader(
        barbersRecurringBlocksActionTypes.SAVE_FAILURE,
    );
    const [isClearError, clearError] = useErrorLoader(
        barbersRecurringBlocksActionTypes.CLEAR_FAILURE,
    );

    // Pick a default barber once barbers load
    useEffect(() => {
        if (activeBarberId == null && barbers && barbers.length) {
            setActiveBarberId(barbers[0].id);
        }
    }, [barbers, activeBarberId]);

    const activeBarber = useMemo(
        () => barbers?.find((b) => b.id === activeBarberId),
        [barbers, activeBarberId],
    );

    // Sync local grid state from the active barber whenever it changes
    const initialBlocked = useMemo(() => {
        const set = new Set();
        activeBarber?.recurringBlocks?.forEach((rb) => {
            set.add(cellKey(rb.dayOfWeek, normalizeSlotTime(rb.slotTime)));
        });
        return set;
    }, [activeBarber]);

    useEffect(() => {
        setBlocked(new Set(initialBlocked));
    }, [initialBlocked]);

    const isDirty = useMemo(() => {
        if (blocked.size !== initialBlocked.size) return true;
        for (const key of blocked) {
            if (!initialBlocked.has(key)) return true;
        }
        return false;
    }, [blocked, initialBlocked]);

    const onBarberChange = (e) => {
        setActiveBarberId(parseInt(e.target.value));
    };

    const toggleCell = (dayOfWeek, slot) => {
        if (dayOfWeek === 6 && isSaturdayDisabled(slot)) return;
        const key = cellKey(dayOfWeek, slot);
        setBlocked((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const onReset = () => setBlocked(new Set(initialBlocked));

    const onClearAll = () => {
        if (!activeBarber) return;
        const confirmed = window.confirm(
            `Pastro tërësisht orarin javor për ${activeBarber.name}? Ky veprim do të heqë të gjitha intervalet e bllokuara.`,
        );
        if (!confirmed) return;
        dispatchWithPromise(dispatch, barbersRecurringBlocksActions.clearRequest, {
            barberId: activeBarber.id,
        }).catch(() => {
            /* error handled via redux state */
        });
    };

    const onSave = () => {
        if (!activeBarber) return;
        const blocks = [];
        blocked.forEach((key) => {
            const [dow, slot] = key.split("-");
            blocks.push({ dayOfWeek: parseInt(dow), slotTime: slot });
        });
        dispatchWithPromise(dispatch, barbersRecurringBlocksActions.saveRequest, {
            barberId: activeBarber.id,
            blocks,
        }).catch(() => {
            /* error handled via redux state */
        });
    };

    if (!barbers?.length || !activeBarber) {
        return (
            <ContentLayout title="Orari">
                <p>Loading...</p>
            </ContentLayout>
        );
    }

    return (
        <ContentLayout
            title="Orari"
            actions={[
                <BarbersDropdown
                    key="dropdown-schedule"
                    barbers={barbers}
                    selectedBarber={activeBarber.id}
                    onChange={onBarberChange}
                />,
            ]}
        >
            <div className="schedule">
                <div className="schedule__tabs">
                    <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
                </div>

                {activeTab === "recurring" && (
                    <div className="schedule__panel">
                        <p className="schedule__hint">
                            Kliko në një interval për ta bllokuar/zhbllokuar. Intervalet e
                            bllokuara nuk do të jenë të disponueshme për klientët në atë ditë të
                            javës.
                        </p>

                        <div
                            className="schedule-grid"
                            style={{
                                gridTemplateColumns: `auto repeat(${WEEKDAYS.length}, 1fr)`,
                            }}
                            role="grid"
                            aria-label="Orari javor"
                        >
                            <div
                                className="schedule-grid__corner"
                                aria-hidden="true"
                            /> 
                            
                            {WEEKDAYS.map((d) => (
                                <div
                                    key={`head-${d.dayOfWeek}`}
                                    className="schedule-grid__head"
                                    role="columnheader"
                                >
                                    {d.label}
                                </div>
                            ))}

                            {SLOTS.map((slot) => (
                                <React.Fragment key={`row-${slot}`}>
                                    <div className="schedule-grid__row-head" role="rowheader">
                                        {slot}
                                    </div>
                                    {WEEKDAYS.map((d) => {
                                        const isClosed =
                                            d.dayOfWeek === 6 && isSaturdayDisabled(slot);
                                        const isBlocked = blocked.has(
                                            cellKey(d.dayOfWeek, slot),
                                        );
                                        const className = [
                                            "schedule-grid__cell",
                                            isClosed && "schedule-grid__cell--closed",
                                            isBlocked && "schedule-grid__cell--blocked",
                                        ]
                                            .filter(Boolean)
                                            .join(" ");
                                        return (
                                            <button
                                                type="button"
                                                key={cellKey(d.dayOfWeek, slot)}
                                                className={className}
                                                onClick={() => toggleCell(d.dayOfWeek, slot)}
                                                disabled={isClosed}
                                                aria-pressed={isBlocked}
                                                aria-label={`${d.label} ${slot}${
                                                    isBlocked ? " (e bllokuar)" : ""
                                                }`}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="schedule__legend">
                            <span className="schedule__legend-item">
                                <span className="schedule__legend-swatch schedule__legend-swatch--free" />
                                I lirë
                            </span>
                            <span className="schedule__legend-item">
                                <span className="schedule__legend-swatch schedule__legend-swatch--blocked" />
                                I bllokuar
                            </span>
                            <span className="schedule__legend-item">
                                <span className="schedule__legend-swatch schedule__legend-swatch--closed" />
                                Mbyllur
                            </span>
                        </div>

                        <div className="schedule__actions">
                            <Button
                                onClick={onClearAll}
                                type="rounded"
                                disabled={
                                    initialBlocked.size === 0 || isSaving || isClearing
                                }
                                className="schedule__clear"
                            >
                                {isClearing ? "Duke pastruar..." : "Pastro të gjitha"}
                            </Button>
                            <Button
                                onClick={onReset}
                                type="rounded"
                                disabled={!isDirty || isSaving || isClearing}
                                className="schedule__reset"
                            >
                                Anulo ndryshimet
                            </Button>
                            <Button
                                onClick={onSave}
                                disabled={!isDirty || isSaving || isClearing}
                            >
                                {isSaving ? "Duke ruajtur..." : "Ruaj orarin"}
                            </Button>
                        </div>

                        {isSaveError && (
                            <Error
                                size="medium"
                                message={
                                    saveError?.[0]?.message || "Orari nuk mund të ruhet"
                                }
                            />
                        )}
                        {isClearError && (
                            <Error
                                size="medium"
                                message={
                                    clearError?.[0]?.message || "Orari nuk mund të pastrohet"
                                }
                            />
                        )}
                    </div>
                )}

                {activeTab === "days-off" && (
                    <div className="schedule__panel">
                        <p className="schedule__hint">
                            Zgjedh një ditë të caktuar për ta bllokuar tërësisht (psh. pushim,
                            sëmundje, festë).
                        </p>
                        <BlockedDays barber={activeBarber} />
                    </div>
                )}
            </div>
        </ContentLayout>
    );
};

export default Schedule;
